import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Folder, ExternalLink, Download, ChevronLeft, Loader2, CalendarClock } from 'lucide-react';

import { useConfiguredBrandsQuery } from '../../queries/brandMetrics';
import brandMetricsApi from '../../api/brandMetrics';

const MONTHS_ES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
  'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const START = '2026-07'; // primer mes con datos migrados

const monthLabel = (p) => { const [y, m] = p.split('-'); return `${MONTHS_ES[+m]} ${y}`; };

// Carpetas de mes: desde el mes actual hacia atrás hasta START (agosto aparece solo al llegar).
const monthFolders = () => {
  const out = [];
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  const [sy, sm] = START.split('-').map(Number);
  while (y > sy || (y === sy && m >= sm)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
    if (out.length > 18) break;
  }
  return out;
};

const lastClosedMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');

/**
 * Carpeta de reportes mensuales. Nivel 1: carpetas por mes; nivel 2: todas las
 * marcas del mes, cada una se genera on-demand (mismos números que el panel) para
 * previsualizar (editar + descargar en el propio reporte) o descargar directo.
 */
export default function AdminReports() {
  const { data: brands = [] } = useConfiguredBrandsQuery();
  const [period, setPeriod] = useState(lastClosedMonth);
  const [openBrand, setOpenBrand] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [type, setType] = useState('mensual'); // 'mensual' (mes total) | 'quincenal' (mitad de mes)
  const [compareInput, setCompareInput] = useState(''); // mes elegido en el selector (aún sin aplicar)
  const [compare, setCompare] = useState(null); // mes de comparación aplicado (dispara la regeneración)

  const folders = monthFolders();

  const resetCompare = () => { setCompare(null); setCompareInput(''); };

  const { data: html, isFetching, isError, error } = useQuery({
    queryKey: ['report', type, openBrand?.id, period, compare],
    queryFn: () => brandMetricsApi.getMonthlyReportHtml(openBrand.id, period, type, compare),
    enabled: Boolean(openBrand),
    retry: false,
  });
  const notFound = isError && error?.response?.status === 404;

  const openInNewTab = () => {
    if (!html) return;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
  };

  const downloadReport = async (brand) => {
    setDownloadingId(brand.id);
    try {
      const doc = await brandMetricsApi.getMonthlyReportHtml(brand.id, period, type);
      const url = URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-${type === 'quincenal' ? 'quincenal-' : ''}${slug(brand.name)}-${period}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // el reporte no está disponible para ese mes; el usuario puede abrirlo para ver el detalle
    }
    setDownloadingId(null);
  };

  return (
    <div className="mx-auto max-w-[1120px] px-4 py-6">
      <div className="mb-5 flex items-center gap-3">
        <FileText className="h-6 w-6 text-blue-600" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reportes</h1>
          <p className="text-sm text-gray-500">
            Elegí mes y tipo, y abrí el reporte de cada cliente para editar y descargar.
          </p>
        </div>
      </div>

      {/* Carpetas por mes */}
      <div className="mb-6 flex flex-wrap gap-2">
        {folders.map((p) => {
          const active = p === period;
          return (
            <button
              key={p}
              type="button"
              onClick={() => { setPeriod(p); setOpenBrand(null); resetCompare(); }}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Folder className="h-4 w-4" />
              {monthLabel(p)}
            </button>
          );
        })}
      </div>

      {/* Tipo de reporte: Mes total / Quincenal */}
      <div className="mb-6 flex gap-2">
        {[['mensual', 'Mes total', FileText], ['quincenal', 'Quincenal', CalendarClock]].map(([t, label, Icon]) => {
          const active = t === type;
          return (
            <button
              key={t}
              type="button"
              onClick={() => { setType(t); setOpenBrand(null); resetCompare(); }}
              className={`inline-flex items-center gap-2 rounded-lg border px-3.5 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-blue-600 bg-blue-50 font-medium text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      {!openBrand && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {brands.map((b) => (
            <div
              key={b.id}
              className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <button
                type="button"
                onClick={() => { setOpenBrand(b); resetCompare(); }}
                className="flex min-w-0 items-center gap-3 text-left"
              >
                <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-blue-50 text-blue-600">
                  <FileText className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-900">{b.name}</span>
                  <span className="block text-xs text-gray-400">
                    {monthLabel(period)} · {type === 'quincenal' ? 'Quincenal' : 'Mes total'}
                  </span>
                </span>
              </button>
              <button
                type="button"
                title="Descargar HTML"
                onClick={() => downloadReport(b)}
                disabled={downloadingId === b.id}
                className="ml-2 grid h-9 w-9 flex-none place-items-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {downloadingId === b.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {openBrand && (
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => { setOpenBrand(null); resetCompare(); }}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ChevronLeft className="h-4 w-4" />
              {monthLabel(period)}
            </button>
            <span className="font-medium text-gray-900">{openBrand.name}</span>
            {isFetching && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
            {html && !isFetching && (
              <button
                type="button"
                onClick={openInNewTab}
                className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Abrir en pestaña
              </button>
            )}
          </div>

          {type === 'mensual' && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                <CalendarClock className="h-4 w-4" />
                Comparar contra
              </span>
              <input
                type="month"
                value={compareInput}
                max={period}
                onChange={(e) => setCompareInput(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
              />
              <button
                type="button"
                disabled={!compareInput || compareInput === compare}
                onClick={() => setCompare(compareInput)}
                className="inline-flex items-center gap-1.5 rounded-md border border-blue-600 bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Regenerar
              </button>
              {compare && (
                <button
                  type="button"
                  onClick={resetCompare}
                  className="text-sm text-gray-500 underline hover:text-gray-700"
                >
                  Volver al automático
                </button>
              )}
              {compare && (
                <span className="text-xs text-gray-500">Comparando contra {monthLabel(compare)}</span>
              )}
            </div>
          )}

          {notFound && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
              {openBrand.name} no tiene datos para {monthLabel(period)} todavía.
            </div>
          )}
          {isError && !notFound && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
              No se pudo generar el reporte. Probá de nuevo en unos segundos.
            </div>
          )}
          {html && !isError && (
            <>
              <p className="mb-2 text-xs text-gray-400">
                Tip: hacé clic en los textos resaltados para editarlos y usá el botón{' '}
                <span className="font-medium text-blue-600">Descargar HTML</span> del reporte para
                guardar tu versión editada.
              </p>
              <iframe
                title="Reporte mensual"
                srcDoc={html}
                className="w-full rounded-xl border border-gray-200 bg-white"
                style={{ height: '80vh' }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
